/* Here are all the API Response models for the module instance */
/* The naming convention is `${EndpointName}Response` where EndpointName is without the prefix ('Get', 'Set') */

import {
  IdValueType,
  KeyValueIsDefaultModel,
  KeyValueRoleModel,
  KeyValueType,
  KeyValueUserModel,
} from '@models/common';
import { EIBicRoleModel } from '@models/module/entityInstance';
import { FormFieldModel } from '@models/module/fields/main';
import { NotifyEmailsAndRolesModel } from '@module-instance/dynamic-module/common/dm.model';
import { QueueJobType } from './queue.model';

export type ModuleInstanceRequestParams<B = unknown> = {
  actionInstanceId?: number;
  /** Be aware of this - may be document entityInstanceId or contract entityInstanceId */
  entityInstanceId?: number;
  fieldId?: number;
  parentEntityInstanceId?: number;
  body?: B;
  contractId?: number;
  moduleId?: number;
  workflowId?: number;
  entityId?: number;
  actionId?: number;
  transitionId?: number;
  screenId?: number;
  ids?: number[];
  roleId?: number;
  noFields?: boolean;
  editMode?: boolean;
  documentNumber?: number;
  paymentPeriodId?: number;
  moduleTypeId?: number;
  sovEntityInstanceId?: number;
  presetId?: number;
  draftValues?: IdValueType<any, number>[];
  notifyUsers?: NotifyEmailsAndRolesModel;
};

export type HeaderEntityInstancesResponse = {
  assignee: KeyValueRoleModel<string, string>;
  ballInCourt: EIBicRoleModel[];
  contractName: string;
  contractNumber: string;
  entityInstanceId: number;
  moduleAbbr: string;
  moduleDescription: string;
  moduleDescriptionField: string;
  moduleDocumentNumber: string;
  originalModuleDocumentNumber?: string;
  moduleName: string;
  projectName: string;
  projectNumber: string;
  workflowGroup: string;
  state: string;
  stateColor: string;
  stateTextColor: string;
};

export interface AssignmentEntityInstanceResponse {
  manualAssignUsers: KeyValueRoleModel<string, string>[];
  assignee: KeyValueRoleModel<string, string>;
}

export type FieldsEntityInstanceResponse = {
  autosaveEnabled: boolean;
  canChangeAssignment: boolean;
  conditionalFieldIds: number[];
  conditionalFieldsOnScreen: boolean;
  // currency: Currency;
  emails: string[];
  emailsDataSource: KeyValueUserModel<string>[];
  fields: FormFieldModel[];
  hasFields: boolean;
  hideRolesNotify: boolean;
  isEmailAction: boolean;
  manualAssignUsers: KeyValueRoleModel<string, string>[];
  requiresNonBindingSignature: boolean;
  roleToExcludeIds: number[];
  rolesDict: KeyValueType[];
  // screenId: number;
  screenLabel?: string;
  transitionLeavingDraft: boolean;
  workflowGroups: KeyValueIsDefaultModel<number, string>[];
  isDraft?: boolean;
};

export type MultipleTransitionInfoRequest = {
  entityInstanceId: number;
  paymentPeriodId?: number;
  sovEntityInstanceId?: number;
  isDraft?: boolean;
};

export type SaveTransitionScreenBody = {
  emails: string[];
  groups: string[];
  documentNumber?: number;
  screenId?: number;
  roleToExcludeIds: number[];
  values?: IdValueType<any, number>[];
  startScreenValues?: IdValueType<any, number>[];
  includeAttachments?: boolean;
  manualAssignUser?: KeyValueRoleModel<string, string>;
  workflowGroupId?: number;
};

export type SaveMultipleTransitionScreensBody = SaveTransitionScreenBody & {
  entityInstanceIds?: number[];
  presetIds?: number[];
  info?: MultipleTransitionInfoRequest[];
};

export type TriggerMultipleTransitionScreensOrTemplatesRequest = {
  presetIds: number[];
  info: MultipleTransitionInfoRequest[];
  contractId: number;
  workflowId: number;
  moduleId: number;
  transitionId: number;
  isAutoTrigger: boolean;
};

export type TriggerMultipleTransitionScreensOrTemplatesResult = {
  entityInstanceIds: number[];
  fullDocNumsWithRequiredFields: string[];
  isAutoTrigger: boolean;
  signatureIsRequired: boolean;
};

export type BackgroundDownloadInstancesRequestParams = ModuleInstanceRequestParams & {
  title: string;
  templateId?: number;
  isIncludeActivity?: boolean;
  isIncludeDocumentAttachments?: boolean;
  isIncludeActivityAttachments?: boolean;
  isBundleDocuments?: boolean;
};

export type GetAttachmentsSizeRequestParams = ModuleInstanceRequestParams & {
  isIncludeActivity?: boolean;
  isIncludeDocumentAttachments?: boolean;
  isIncludeActivityAttachments?: boolean;
};

export type ValidatePrintAttachmentsRequestParams = ModuleInstanceRequestParams<number[]> & {
  isIncludeActivity?: boolean;
  isIncludeDocumentAttachments?: boolean;
  isIncludeActivityAttachments?: boolean;
};

export type ExcludedAttachmentItem = {
  entityInstanceId: number;
  documentNumber: string;
  fileName: string;
  category: string;
  reason: 'unsupportedFormat' | 'missingExtension' | 'emptyFile' | string;
};

export type PrintAttachmentValidationResult = {
  totalSizeBytes: number;
  exceedsSizeLimit: boolean;
  supportedCount: number;
  unsupportedCount: number;
  unsupportedAttachments: ExcludedAttachmentItem[];
};

export type GetInstancesAsPdfRequestParams = ModuleInstanceRequestParams & {
  title: string;
  templateId?: number;
  isIncludeActivity?: boolean;
  isIncludeDocumentAttachments?: boolean;
  isIncludeActivityAttachments?: boolean;
  isBundleDocuments?: boolean;
  jobType?: QueueJobType;
};

export type MultipleModuleScreenValuesModel = {
  entityInstanceIds: number[];
  values: SaveTransitionScreenBody;
};
